import { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Grid,
  Skeleton,
  Typography,
} from '@mui/material';
import {
  ArrowForward,
  Hub,
  LinkOff,
} from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import ListHeader from '../components/ListHeader';
import { usePermissions } from '../contexts/PermissionContext';
import usePageTitle from '../hooks/usePageTitle';
import { fetchContactZBootstrap } from '../services/contactZ';
import contactZLogo from '../assets/integrations/contact-z/contact-z-logo.svg';

import type { ContactZBootstrapPayload } from '../types';

export default function IntegrationsHubPage() {
  const { t } = useTranslation(['pages', 'common']);
  const { hasPermission } = usePermissions();
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [bootstrapData, setBootstrapData] = useState<ContactZBootstrapPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const canAccessContactZ = hasPermission('contact_z_access');
  const contactZAccounts = bootstrapData?.entitlements.accounts;
  const remainingContactZConnections = contactZAccounts?.remaining ?? 0;
  const totalContactZConnections = contactZAccounts?.limit ?? 0;
  const hasUnlimitedContactZConnections = contactZAccounts?.remaining == null;

  usePageTitle(t('pages:integrations.title'));

  const loadIntegration = useCallback(async () => {
    if (!canAccessContactZ) {
      setBootstrapData(null);
      setError(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const nextBootstrap = await fetchContactZBootstrap();
      setBootstrapData(nextBootstrap);
    } catch (loadError) {
      console.error('Failed to load Contact Z integration overview:', loadError);
      setBootstrapData(null);
      setError(t('pages:integrations.errors.loading'));
    } finally {
      setLoading(false);
    }
  }, [canAccessContactZ, t]);

  useEffect(() => {
    void loadIntegration();
  }, [loadIntegration]);

  return (
    <Box>
      <ListHeader
        icon={<Hub sx={{ fontSize: 32, color: 'primary.main' }} />}
        title={t('pages:integrations.title')}
        subtitle={t('pages:integrations.subtitle')}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          {loading ? (
            <Card sx={{ borderRadius: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Skeleton variant="rectangular" height={64} sx={{ borderRadius: 2, mb: 3 }} />
                <Skeleton height={32} width="60%" />
                <Skeleton height={20} width="90%" />
                <Skeleton height={20} width="70%" />
              </CardContent>
            </Card>
          ) : (
            <Card
              sx={{
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
                boxShadow:
                  theme.palette.mode === 'dark'
                    ? '0 28px 50px rgba(0,0,0,0.45)'
                    : '0 20px 40px rgba(0,0,0,0.08)',
                minHeight: '100%',
              }}
            >
              <CardActionArea
                component={canAccessContactZ ? RouterLink : 'div'}
                to={canAccessContactZ ? '/integrations/contact-z' : undefined}
                sx={{
                  alignItems: 'stretch',
                  display: 'flex',
                  height: '100%',
                  opacity: canAccessContactZ ? 1 : 0.72,
                  pointerEvents: canAccessContactZ ? 'auto' : 'none',
                }}
              >
                <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, p: 3, width: '100%' }}>
                  <Box
                    sx={{
                      alignItems: 'center',
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 2,
                    }}
                  >
                    <Box
                      component="img"
                      src={contactZLogo}
                      alt="Contact Z"
                      sx={{
                        display: 'block',
                        maxWidth: '100%',
                        width: 180,
                      }}
                    />
                    <Chip
                      color={bootstrapData?.integration.configured ? 'success' : 'default'}
                      label={
                        canAccessContactZ
                          ? (bootstrapData?.integration.configured
                            ? t('pages:integrations.contactZ.card.status.ready')
                            : t('pages:integrations.contactZ.card.status.notConfigured'))
                          : t('pages:integrations.contactZ.card.status.noAccess')
                      }
                      size="small"
                      variant={bootstrapData?.integration.configured ? 'filled' : 'outlined'}
                    />
                  </Box>

                  <Box>
                    <Typography variant="h5" fontWeight={700} gutterBottom>
                      {t('pages:integrations.contactZ.title')}
                    </Typography>
                    <Typography color="text.secondary" variant="body2">
                      {t('pages:integrations.contactZ.card.description')}
                    </Typography>
                  </Box>

                  {canAccessContactZ && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      <Chip
                        label={
                          hasUnlimitedContactZConnections
                            ? t('pages:integrations.contactZ.card.availableConnectionsUnlimited')
                            : t('pages:integrations.contactZ.card.availableConnections', {
                              available: remainingContactZConnections,
                              total: totalContactZConnections,
                            })
                        }
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  )}

                  <Box sx={{ mt: 'auto', pt: 1 }}>
                    {canAccessContactZ ? (
                      <Button
                        endIcon={<ArrowForward />}
                        sx={{ px: 0 }}
                      >
                        {t('pages:integrations.contactZ.card.open')}
                      </Button>
                    ) : (
                      <Box sx={{ alignItems: 'center', color: 'text.secondary', display: 'flex', gap: 1 }}>
                        <LinkOff fontSize="small" />
                        <Typography variant="body2">
                          {t('pages:integrations.contactZ.card.noAccessDescription')}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}
